<template>
	<div
		class="participant"
		:class="{ active, 'audio-only': isAudioOnly }"
		:id="participant.id"
		@dblclick="$emit('pin-stream', participant.id)"
	>
		<div class="video-container">
			<Subscriber v-if="participant.isSubscriber" :stream="participant.stream" />
			<Publisher
				v-if="participant.isPublishing"
				videoSource="video"
				@stream="handleStream"
			/>
			<Publisher
				v-if="participant.isSharing"
				videoSource="screen"
				@stream="handleStream"
			/>
		</div>
		<div class="participant-interior">
			<div class="participant-info">
				<div class="participant-label" :class="{ muted: isMuted }">
					<MicOffIcon
						class="mic-off"
						color="#ff112c"
						v-if="isMuted && !isScreenShare"
					/>
					<DesktopIcon class="screenshare" v-if="isScreenShare" />
					<span v-text="user.fullname" />
				</div>
				<div
					class="participant-pin"
					:class="{ pinned }"
					@click="$emit('pin-stream', participant.id)"
					@keyup.enter="$emit('pin-stream', participant.id)"
					:aria-label="pinned ? $t('meetings_unpin') : $t('meetings_pin')"
					tabindex="0"
					role="button"
				>
					<PinIcon class="pin noSelect" />
					<div class="noSelect">
						{{ pinned ? $t('meetings_unpin') : $t('meetings_pin') }}
					</div>
				</div>
			</div>
			<div class="participant-audio-only" v-if="isAudioOnly">
				<div class="audio-only-content">
					<ProfilePic
						:user="user"
						class="profile-pic"
						:size="profileWidth + 'px'"
						alt="profile"
					/>
					<div class="audio-only-label" v-text="videoLabel" />
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { MicOffIcon, PinIcon, DesktopIcon } from '@/icons'
import Subscriber from './Subscriber'
import Publisher from './Publisher'
import { ProfilePic } from '../common'
import {
	getPublisher,
	getScreenPublisher,
	getUserById,
	getStream,
} from './data'

export default {
	name: 'Stream',
	components: {
		MicOffIcon,
		Subscriber,
		Publisher,
		ProfilePic,
		PinIcon,
		DesktopIcon,
	},
	props: {
		participant: {
			type: Object,
			default: () => ({}),
		},
		pinned: {
			type: Boolean,
			default: null,
		},
		active: {
			type: Boolean,
			default: null,
		},
		profileWidth: {
			type: Number,
			default: 0,
		},
	},

	data() {
		return {
			publisherStream: null,
			stream: this.getUpdatedStream(),
		}
	},

	emits: ['pin-stream', 'element'],

	mounted() {
		this.$emit('element', this.$el)

		this.$onEventBus({
			streamUpdate: () => {
				this.stream = this.getUpdatedStream()
			},
		})
	},

	methods: {
		handleStream(stream) {
			this.publisherStream = stream
		},
		getStreamId() {
			if (this.publisherStream) {
				return this.publisherStream.streamId
			}
			const { isPublishing, isSharing, stream } = this.participant
			if (stream) {
				return stream.streamId
			}
			if (isPublishing) {
				const pub = getPublisher() || {}
				return pub ? pub.streamId : null
			}
			if (isSharing) {
				const screenPub = getScreenPublisher() || {}
				return screenPub ? screenPub.streamId : null
			}
			return null
		},
		getUpdatedStream() {
			const id = this.getStreamId()
			return Object.assign({}, getStream(id))
		},
	},

	computed: {
		videoLabel() {
			if (!this.stream?.streamId)
				return this.$t('meetings_streamer_status_connecting')
			if (this.stream.hasOwnProperty('disabledReason')) {
				switch (this.stream.disabledReason) {
					case 'quality':
					case 'codecNotSupported':
						return this.$t('meetings_streamer_status_poorconnection')
				}
			}
			return this.$t('meetings_streamer_status_audioonly')
		},
		user() {
			return getUserById(this.participant.userId)
		},
		isMuted() {
			const isMuted = !this.stream || !this.stream.hasAudio
			return isMuted
		},
		isAudioOnly() {
			return (
				!this.stream ||
				!this.stream.hasVideo ||
				(this.stream.hasOwnProperty('disabledReason') &&
					this.stream.disabledReason === 'quality')
			)
		},
		isScreenShare() {
			return this.participant?.isSharing ?? this.stream?.isScreenShare ?? false
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

$gap: 16px;
$dark-gray: #303030;

.participant {
	position: relative;
	cursor: pointer;
	color: white;
	height: 0;
	background: #222;
	font-family: Avenir-Book;
	border: 1px solid $dark-gray;
	&.active {
		.participant-interior {
			border-color: var(--teal-300);
		}
	}
	&.audio-only {
		.video-container {
			display: none;
		}
		.participant-audio-only {
			display: flex;
		}
	}
	.video-container {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		overflow: hidden;
		div {
			object-position: center;
			object-fit: contain;
			width: 100%;
			height: 100%;
		}
	}
	&:hover {
		.participant-pin {
			opacity: 1;
		}
	}
}
.participant-interior {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	z-index: 1;
	display: flex;
	align-items: flex-start;
	margin: -2px;
	justify-content: space-between;
	border: 2px solid rgba(0, 0, 0, 0);
	transition: border-color linear 0.25s;
}
.participant-info {
	width: 100%;
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	font-size: 14px;
	.participant-label {
		height: 28px;
		background: rgba(0, 0, 0, 0.65);
		padding: 4px 8px;
		display: flex;
		align-items: center;
		color: #d4d5d6;
		max-width: calc(100% - 32px); // leave room for pin icon
		.mic-off {
			min-width: 12px;
			margin-right: 8px;
		}
		.screenshare {
			min-width: 20px;
			margin-right: 8px;
		}
		> span {
			text-overflow: ellipsis;
			overflow: hidden;
			white-space: nowrap;
		}
	}
	.participant-pin {
		opacity: 0;
		height: 28px;
		background: rgba(0, 0, 0, 0.65);
		padding: 4px 8px;
		display: flex;
		align-items: center;
		outline: none;
		div {
			margin-left: 8px;
			display: none;
		}
		.pin {
			width: 14px;
			height: 16px;
			margin-top: 2px;
		}
		&.pinned {
			color: var(--teal-300);
			:deep(.pin) g {
				fill: var(--teal-300);
			}
		}
		&:hover {
			color: var(--teal-300);
			:deep(.pin) g {
				fill: var(--teal-300);
			}
		}
	}
}
.participant-audio-only {
	pointer-events: none;
	display: none;
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	align-items: center;
	justify-content: center;
	.audio-only-content {
		display: flex;
		flex-direction: column;
		align-items: center;
		.profile-pic {
			margin-bottom: 16px;
		}
		.audio-only-label {
			font-size: 14px;
			color: gray;
		}
	}
}
</style>
